import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Play, ArrowRight, Calendar } from 'lucide-react';
import { Button } from './ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Latest Sermons strip — pulls the 4 most recent published sermons (which auto-include
 * YouTube videos brought in by the auto-sync background task) and renders them as a strip.
 * Renders nothing if there are no sermons yet.
 */
const LatestSermonsStrip = ({ count = 4 }) => {
  const [sermons, setSermons] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/api/sermons?page=1&limit=${count}`)
      .then((res) => setSermons(res.data.sermons || []))
      .catch(() => setSermons([]))
      .finally(() => setLoading(false));
  }, [count]);

  if (loading || sermons.length === 0) return null;

  return (
    <section className="py-16 sm:py-20 bg-white" data-testid="latest-sermons-section">
      <div className="container mx-auto px-4">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-8 sm:mb-10">
          <div>
            <p className="text-xs sm:text-sm font-semibold text-church-green tracking-wider uppercase mb-1">
              Catch Up
            </p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900">
              Latest Sermons
            </h2>
            <p className="text-sm sm:text-base text-slate-600 mt-2 max-w-xl">
              Missed Sunday? Watch our most recent messages and dig into the Word with us.
            </p>
          </div>
          <Link to="/sermons">
            <Button
              variant="outline"
              className="border-church-green text-church-green hover:bg-church-green hover:text-white"
              data-testid="latest-sermons-view-all"
            >
              View all <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {sermons.map((s) => (
            <SermonCard key={s.id || s.video_url} s={s} />
          ))}
        </div>
      </div>
    </section>
  );
};

const SermonCard = ({ s }) => {
  const dateLabel = s.date
    ? new Date(s.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '';
  const href = s.video_url || '/sermons';
  const external = href.startsWith('http');
  const inner = (
    <>
      <div className="relative aspect-video bg-slate-100 overflow-hidden">
        {s.thumbnail && (
          <img
            src={s.thumbnail}
            alt={`${s.title} — sermon at Life Church of New Jersey in Laurence Harbor, NJ`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        )}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition flex items-center justify-center">
          <div className="w-12 h-12 rounded-full bg-white/90 opacity-0 group-hover:opacity-100 flex items-center justify-center transition">
            <Play className="w-5 h-5 text-church-green ml-0.5" />
          </div>
        </div>
        {s.series && (
          <span className="absolute top-2 left-2 text-[10px] uppercase tracking-wide font-semibold bg-church-green text-white px-2 py-0.5 rounded-full">
            {s.series}
          </span>
        )}
      </div>
      <div className="p-4">
        <h3 className="font-semibold text-slate-900 line-clamp-2 group-hover:text-church-green transition">
          {s.title}
        </h3>
        <div className="flex items-center text-xs text-slate-500 mt-2 gap-3">
          {s.speaker && <span className="truncate">{s.speaker}</span>}
          {dateLabel && (
            <span className="flex items-center gap-1 whitespace-nowrap">
              <Calendar className="w-3 h-3" /> {dateLabel}
            </span>
          )}
        </div>
      </div>
    </>
  );

  const className =
    'group block bg-white rounded-xl overflow-hidden border border-slate-200 hover:border-church-green hover:shadow-lg transition-all';

  return external ? (
    <a href={href} target="_blank" rel="noopener noreferrer" className={className} data-testid="latest-sermon-card">
      {inner}
    </a>
  ) : (
    <Link to={href} className={className} data-testid="latest-sermon-card">
      {inner}
    </Link>
  );
};

export default LatestSermonsStrip;
